/**
 * CommandPaletteHost. Mounted once inside the frame. Owns the palette's
 * open state, the Ctrl+K / Cmd+K shortcut, its data and its actions.
 *
 * Groups, in order:
 *   Recent     the last five things run from the palette (at rest only)
 *   Actions    role-aware and real: each opens the same form modal the pages
 *              use, mounted here so it works from any page
 *   Go to      the role-filtered destinations the sidebar shows
 *   Students / Tutors / Lessons
 *              live `search=` results from the API, debounced, only for
 *              roles allowed to query them; a result opens its page with the
 *              record's drawer via `?id=`
 */

import {
  BookOpenCheck,
  CalendarPlus,
  CalendarRange,
  GraduationCap,
  History,
  Keyboard,
  LogOut,
  Moon,
  Sun,
  UserPlus,
  Users,
  type LucideIcon,
} from "lucide-react";
import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/lib/tenant";
import type { UserRole } from "@/lib/api/auth";
import { useLessons } from "@/lib/api/lessons";
import { useStudents } from "@/lib/api/students";
import { useTutors } from "@/lib/api/tutors";
import { OPEN_PALETTE_EVENT } from "@/lib/commandPalette";
import { openShortcutSheet } from "@/lib/shortcuts";
import { subjectName, yearGroupName } from "@/lib/format/subject";
import { formatDayMonth, formatTime } from "@/lib/format/time";
import { useTheme } from "@/lib/theme";
import { labelFor, visibleNav } from "@/primitives/layout/frame/nav";
import { SignOutDialog } from "@/primitives/layout/frame/SignOutDialog";
import { CommandPalette, type PaletteGroup, type PaletteItem } from "@/primitives/overlays/CommandPalette";
import { ROUTES } from "@/types/routes";

import { scopedKey } from "@/lib/tenant/basePath";
// The form modals load on first use, so the frame (and the login chunk it
// shares) never carries four forms nobody has opened yet.
const LessonFormModal = lazy(() =>
  import("@/pages/lessons/LessonFormModal").then((m) => ({ default: m.LessonFormModal })),
);
const HomeworkFormModal = lazy(() =>
  import("@/pages/homework/HomeworkFormModal").then((m) => ({ default: m.HomeworkFormModal })),
);
const StudentFormModal = lazy(() =>
  import("@/pages/students/StudentFormModal").then((m) => ({ default: m.StudentFormModal })),
);
const TutorFormModal = lazy(() =>
  import("@/pages/tutors/TutorFormModal").then((m) => ({ default: m.TutorFormModal })),
);

type ActionKey =
  | "new-lesson"
  | "set-homework"
  | "add-student"
  | "add-tutor"
  | "toggle-theme"
  | "shortcuts"
  | "sign-out";
type ModalKey = "lesson" | "homework" | "student" | "tutor" | null;

interface RecentEntry {
  id: string;
  kind: "nav" | "record" | "action";
  label: string;
  sub?: string;
  icon: IconKey;
  href?: string;
  action?: ActionKey;
}

type IconKey = "calendar" | "student" | "tutor" | "action" | "nav";

const RECENT_KEY = scopedKey("drtutor.palette.recent");
const RECENT_MAX = 5;

function readRecents(): RecentEntry[] {
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    return raw ? (JSON.parse(raw) as RecentEntry[]).slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

function writeRecents(list: RecentEntry[]): void {
  try {
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
  } catch {
    /* Storage unavailable: recents last for this page view only. */
  }
}

/**
 * Contiguous match beats word-start beats in-order letters; 0 is no match.
 * In-order letters only count when they sit close together (for typos such
 * as "lsns" for "lessons"), so "sam" does not match "Switch to dark mode".
 */
function fuzzyScore(text: string, q: string): number {
  const t = text.toLowerCase();
  const needle = q.toLowerCase().trim();
  if (!needle) return 1;
  const at = t.indexOf(needle);
  if (at === 0) return 100;
  if (at > 0) return t[at - 1] === " " ? 80 : 60 - Math.min(at, 30);
  const first = t.indexOf(needle[0] ?? "");
  if (first < 0) return 0;
  let i = 0;
  let last = first;
  for (let k = first; k < t.length && i < needle.length; k++) {
    if (t[k] === needle[i]) {
      i += 1;
      last = k;
    }
  }
  const span = last - first + 1;
  return i === needle.length && span <= needle.length * 2 ? 10 : 0;
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return v;
}

const ICONS: Record<IconKey, LucideIcon> = {
  calendar: CalendarRange,
  student: GraduationCap,
  tutor: Users,
  action: CalendarPlus,
  nav: History,
};

export function CommandPaletteHost() {
  const { user } = useAuth();
  const role = user?.role as UserRole | undefined;
  const tenantFeatures = useTenant().features;
  const navigate = useNavigate();
  const { resolved, setMode } = useTheme();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [modal, setModalState] = useState<ModalKey>(null);
  const [signOutOpen, setSignOutOpen] = useState(false);
  // Each form modal fetches its lookups while mounted, so it mounts on first
  // use only, then stays mounted so its close animation can play.
  const [used, setUsed] = useState<Set<Exclude<ModalKey, null>>>(() => new Set());
  const setModal = useCallback((m: ModalKey) => {
    if (m) setUsed((prev) => (prev.has(m) ? prev : new Set(prev).add(m)));
    setModalState(m);
  }, []);
  const [recents, setRecents] = useState<RecentEntry[]>(() => readRecents());

  const close = useCallback(() => setOpen(false), []);

  // Ctrl+K / Cmd+K anywhere, and the trigger event.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener(OPEN_PALETTE_EVENT, onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(OPEN_PALETTE_EVENT, onOpen);
    };
  }, []);

  // Each opening starts fresh.
  useEffect(() => {
    if (open) {
      setQuery("");
      setRecents(readRecents());
    }
  }, [open]);

  const remember = useCallback((entry: RecentEntry) => {
    const next = [entry, ...readRecents().filter((r) => r.id !== entry.id)];
    writeRecents(next);
    setRecents(next.slice(0, RECENT_MAX));
  }, []);

  /* ------------------------------------------------------------ actions */

  const isAdmin = role === "admin";
  const isTutor = role === "tutor";
  const canTeach = isAdmin || isTutor;

  const runAction = useCallback(
    (key: ActionKey) => {
      switch (key) {
        case "new-lesson":
          setModal("lesson");
          break;
        case "set-homework":
          setModal("homework");
          break;
        case "add-student":
          setModal("student");
          break;
        case "add-tutor":
          setModal("tutor");
          break;
        case "toggle-theme":
          setMode(resolved === "dark" ? "light" : "dark");
          break;
        case "shortcuts":
          // After the palette has closed, so the sheet takes focus cleanly.
          window.setTimeout(openShortcutSheet, 0);
          break;
        case "sign-out":
          setSignOutOpen(true);
          break;
      }
    },
    [resolved, setMode, setModal],
  );

  const actionDefs = useMemo(() => {
    const list: Array<{ key: ActionKey; label: string; sub: string; icon: LucideIcon; keywords: string }> = [];
    if (canTeach) {
      list.push(
        { key: "new-lesson", label: "New lesson", sub: "Schedule a lesson on the timetable", icon: CalendarPlus, keywords: "schedule book create" },
        { key: "set-homework", label: "Set homework", sub: "Assign work to students", icon: BookOpenCheck, keywords: "assign create task" },
        { key: "add-student", label: "Add a student", sub: "Enrol a new learner", icon: GraduationCap, keywords: "enrol create pupil" },
      );
    }
    if (isAdmin) {
      list.push({ key: "add-tutor", label: "Add a tutor", sub: "Invite a new tutor onto the platform", icon: UserPlus, keywords: "invite create teacher" });
    }
    list.push(
      {
        key: "toggle-theme",
        label: resolved === "dark" ? "Switch to light mode" : "Switch to dark mode",
        sub: "Change the appearance",
        icon: resolved === "dark" ? Sun : Moon,
        keywords: "theme dark light appearance",
      },
      { key: "shortcuts", label: "Keyboard shortcuts", sub: "Every shortcut, at a glance", icon: Keyboard, keywords: "keys hotkeys help" },
      { key: "sign-out", label: "Sign out", sub: user?.email ?? "", icon: LogOut, keywords: "log out logout exit" },
    );
    return list;
  }, [canTeach, isAdmin, resolved, user?.email]);

  /* ------------------------------------------------------------ search */

  const q = query.trim();
  const debounced = useDebounced(q, 250);
  const searching = debounced.length >= 2;
  const pending = q.length >= 2 && q !== debounced;

  const canSearchStudents = role === "admin" || role === "tutor" || role === "parent";
  const studentsQ = useStudents({ search: debounced, page_size: 5 }, open && searching && canSearchStudents);
  const tutorsQ = useTutors({ search: debounced, page_size: 5 }, open && searching && isAdmin);
  const lessonsQ = useLessons({ search: debounced, page_size: 5, ordering: "-scheduled_at" }, open && searching && !!role);

  /* ------------------------------------------------------------ groups */

  const groups = useMemo<PaletteGroup[]>(() => {
    const out: PaletteGroup[] = [];
    const rank = <T,>(items: T[], text: (t: T) => string) =>
      items
        .map((it) => ({ it, s: fuzzyScore(text(it), q) }))
        .filter((x) => x.s > 0)
        .sort((a, b) => b.s - a.s)
        .map((x) => x.it);

    if (!q && recents.length) {
      out.push({
        label: "Recent",
        items: recents.map((r) => ({
          id: `recent:${r.id}`,
          label: r.label,
          sub: r.sub,
          icon: ICONS[r.icon] ?? History,
          hint: r.kind === "action" ? "Action" : r.kind === "nav" ? "Page" : undefined,
          run: () => {
            remember(r);
            if (r.kind === "action" && r.action) runAction(r.action);
            else if (r.href) navigate(r.href);
          },
        })),
      });
    }

    const actions = rank(actionDefs, (a) => `${a.label} ${a.keywords}`);
    out.push({
      label: "Actions",
      items: actions.map((a) => ({
        id: `action:${a.key}`,
        label: a.label,
        sub: a.sub,
        icon: a.icon,
        hint: "Action",
        run: () => {
          if (a.key !== "toggle-theme" && a.key !== "sign-out" && a.key !== "shortcuts") {
            remember({ id: `action:${a.key}`, kind: "action", label: a.label, sub: a.sub, icon: "action", action: a.key });
          }
          runAction(a.key);
        },
      })),
    });

    const nav = rank(visibleNav(role, tenantFeatures), (n) => labelFor(n, role));
    out.push({
      label: "Go to",
      items: nav.map((n) => {
        const label = labelFor(n, role);
        return {
          id: `nav:${n.to}`,
          label,
          icon: n.icon,
          hint: "Page",
          run: () => {
            remember({ id: `nav:${n.to}`, kind: "nav", label, icon: "nav", href: n.to });
            navigate(n.to);
          },
        };
      }),
    });

    if (q.length >= 2) {
      if (canSearchStudents) {
        const items: PaletteItem[] = searching && !pending
          ? (studentsQ.data?.results ?? []).map((s) => {
              const label = s.full_name || [s.first_name, s.last_name].filter(Boolean).join(" ") || s.email || "Student";
              const sub = [yearGroupName(s.year_group), s.school_name].filter(Boolean).join(", ");
              const href = `${ROUTES.STUDENTS}?id=${s.id}`;
              return {
                id: `student:${s.id}`,
                label,
                sub,
                icon: GraduationCap,
                run: () => {
                  remember({ id: `student:${s.id}`, kind: "record", label, sub, icon: "student", href });
                  navigate(href);
                },
              };
            })
          : [];
        out.push({ label: "Students", items, loading: pending || studentsQ.isFetching });
      }
      if (isAdmin) {
        const items: PaletteItem[] = searching && !pending
          ? (tutorsQ.data?.results ?? []).map((t) => {
              const label = t.full_name || t.email || "Tutor";
              const sub = (t.subjects ?? []).map(subjectName).join(", ");
              const href = `${ROUTES.TUTORS}?id=${t.id}`;
              return {
                id: `tutor:${t.id}`,
                label,
                sub,
                icon: Users,
                run: () => {
                  remember({ id: `tutor:${t.id}`, kind: "record", label, sub, icon: "tutor", href });
                  navigate(href);
                },
              };
            })
          : [];
        out.push({ label: "Tutors", items, loading: pending || tutorsQ.isFetching });
      }
      const lessonItems: PaletteItem[] = searching && !pending
        ? (lessonsQ.data?.results ?? []).map((l) => {
            const label = l.title || subjectName(l.subject) || "Lesson";
            const sub = [`${formatDayMonth(l.scheduled_at)}, ${formatTime(l.scheduled_at)}`, l.tutor_name].filter(Boolean).join(" · ");
            const href = `${ROUTES.LESSONS}?id=${l.id}`;
            return {
              id: `lesson:${l.id}`,
              label,
              sub,
              icon: CalendarRange,
              run: () => {
                remember({ id: `lesson:${l.id}`, kind: "record", label, sub, icon: "calendar", href });
                navigate(href);
              },
            };
          })
        : [];
      out.push({ label: "Lessons", items: lessonItems, loading: pending || lessonsQ.isFetching });
    }

    return out;
  }, [
    q,
    recents,
    actionDefs,
    role,
    tenantFeatures,
    canSearchStudents,
    isAdmin,
    searching,
    pending,
    studentsQ.data,
    studentsQ.isFetching,
    tutorsQ.data,
    tutorsQ.isFetching,
    lessonsQ.data,
    lessonsQ.isFetching,
    navigate,
    remember,
    runAction,
  ]);

  const anyError = studentsQ.isError || tutorsQ.isError || lessonsQ.isError;

  return (
    <>
      <CommandPalette
        open={open}
        onClose={close}
        query={query}
        onQueryChange={setQuery}
        groups={groups}
        empty={
          <>
            <p className="st-note__title">{q ? `No matches for "${q}"` : "Nothing here yet"}</p>
            <p className="st-note__body" style={{ margin: "6px auto 0" }}>
              {anyError
                ? "Search could not reach the server. Try again in a moment."
                : "Try a student, a tutor, a lesson title or a page name."}
            </p>
          </>
        }
      />

      <Suspense fallback={null}>
        {canTeach && used.has("lesson") ? (
          <LessonFormModal
            open={modal === "lesson"}
            lesson={null}
            onClose={() => setModal(null)}
            onSaved={() => setModal(null)}
          />
        ) : null}
        {canTeach && used.has("homework") ? (
          <HomeworkFormModal
            open={modal === "homework"}
            homework={null}
            onClose={() => setModal(null)}
            onSaved={() => setModal(null)}
          />
        ) : null}
        {canTeach && used.has("student") ? (
          <StudentFormModal
            open={modal === "student"}
            student={null}
            canAssignTutor={isAdmin}
            onClose={() => setModal(null)}
            onSaved={() => setModal(null)}
          />
        ) : null}
        {isAdmin && used.has("tutor") ? (
          <TutorFormModal open={modal === "tutor"} tutor={null} onClose={() => setModal(null)} onSaved={() => setModal(null)} />
        ) : null}
      </Suspense>
      <SignOutDialog open={signOutOpen} onClose={() => setSignOutOpen(false)} />
    </>
  );
}
